import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Sparkles, Tag, Eye, ExternalLink, Gift } from "lucide-react";
import { api, FILE_URL, formatBRL, displayBrand } from "../../lib/api";

export default function AdminDashboard() {
  // `stats` mirrors the /api/admin/stats response — always live from Mongo,
  // no cache or paginated list undercounting.
  const [stats, setStats] = useState({
    products: { total: 0, unique_active: 0, news_30d: 0 },
    gift_cards: { total: 0, active: 0, total_balance_brl: 0 },
  });
  const [metrics, setMetrics] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/admin/metrics", { params: { days } }).then((r) => setMetrics(r.data));
  }, [days]);

  const cards = [
    { label: "Total de peças", value: stats.products.total, icon: Package },
    { label: "Peças únicas ativas", value: stats.products.unique_active, icon: Tag },
    { label: "Novidades (30d)", value: stats.products.news_30d, icon: Sparkles },
    {
      label: "Gift cards ativos",
      value: `${stats.gift_cards.active} · ${formatBRL(stats.gift_cards.total_balance_brl)}`,
      icon: Gift,
      isText: true,
    },
  ];
  const metricCards = [
    { label: "Visualizações de produto", value: metrics?.product_views || 0, icon: Eye },
    { label: "Cliques 'Comprar no ML'", value: metrics?.ml_clicks || 0, icon: ExternalLink },
    { label: "Try-Ons realizados", value: metrics?.tryons || 0, icon: Sparkles },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="overline text-[#A8A8A8] mb-1">Visão geral</p>
          <h1 className="font-display text-4xl">Dashboard</h1>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="overline border-b border-[#E8E4DF] py-1 bg-transparent" data-testid="metrics-period">
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
        </select>
      </div>

      <div className="grid md:grid-cols-4 gap-5 mb-8">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white p-8" data-testid={`stat-${c.label}`}>
              <Icon size={20} className="text-[#B8A88A] mb-4" />
              <p className={c.isText ? "font-display text-2xl mb-2" : "font-display text-4xl mb-2"}>{c.value}</p>
              <p className="text-sm text-[#6B6B6B]">{c.label}</p>
            </div>
          );
        })}
      </div>

      <h2 className="font-display text-2xl mb-5 mt-12">Engajamento</h2>
      <div className="grid md:grid-cols-3 gap-5 mb-10">
        {metricCards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white p-8" data-testid={`metric-${c.label}`}>
              <Icon size={20} className="text-[#B8A88A] mb-4" />
              <p className="font-display text-4xl mb-2">{c.value}</p>
              <p className="text-sm text-[#6B6B6B]">{c.label}</p>
            </div>
          );
        })}
      </div>

      {metrics?.top_products?.length > 0 && (
        <>
          <h2 className="font-display text-2xl mb-5">Produtos mais vistos</h2>
          <div className="bg-white p-6 mb-10">
            <ul className="divide-y divide-[#E8E4DF]">
              {metrics.top_products.map((p) => (
                <li key={p.id} className="flex items-center gap-4 py-3" data-testid={`top-product-${p.id}`}>
                  <div className="w-12 h-12 bg-[#FAF9F7]">
                    {p.images?.[0] && <img src={FILE_URL(p.images[0])} alt="" className="w-full h-full object-cover"/>}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">{p.name}</p>
                    <p className="text-xs text-[#A8A8A8]">{p.brand} · {formatBRL(p.price)}</p>
                  </div>
                  <span className="text-sm font-display">{p.views} views</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      <div className="bg-white p-8">
        <h3 className="font-display text-xl mb-4">Atalhos</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin/produtos/novo" className="btn-primary">Adicionar peça</Link>
          <Link to="/admin/produtos" className="btn-secondary">Ver produtos</Link>
          <Link to="/admin/cupons" className="btn-secondary">Cupons</Link>
          <Link to="/admin/configuracoes" className="btn-secondary">Configurações</Link>
          <Link to="/admin/gift-cards" className="btn-secondary"><Gift size={14} className="mr-2"/> Gift Cards</Link>
        </div>
      </div>
    </div>
  );
}
